import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { verifyPrescription } from "@/api/appointmentApi";

export default function PrescriptionVerifyPage() {
  const { code } = useParams<{ code: string }>();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["prescription-verify", code],
    queryFn: () => verifyPrescription(code!),
    enabled: !!code,
    retry: false,
  });

  return (
    <Box sx={{ maxWidth: 720, mx: "auto", py: 4, px: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ color: "primary.dark" }}
        >
          Chikitsa
        </Typography>
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ color: "warning.main" }}
        >
          Link
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          Prescription Verification
        </Typography>
      </Stack>

      {isLoading && (
        <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Alert severity="error">
          Could not verify the prescription. Please check the URL and try again.
        </Alert>
      )}

      {data && !data.valid && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <ErrorOutlineIcon color="error" />
            <Typography variant="h6" color="error.main" fontWeight={700}>
              Verification Failed
            </Typography>
          </Stack>
          <Typography color="text.secondary">{data.message}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Code: {data.verificationCode}
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This code does not match any prescription in our system. If you
            believe this is an error, please contact ChikitsaLink support.
          </Alert>
        </Paper>
      )}

      {data && data.valid && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 2 }}
          >
            <VerifiedIcon color="success" />
            <Typography variant="h6" color="success.main" fontWeight={700}>
              Verified Prescription
            </Typography>
            <Chip
              label={data.verificationCode}
              size="small"
              color="success"
              variant="outlined"
            />
          </Stack>

          <Alert severity="success" sx={{ mb: 2 }}>
            {data.message}
          </Alert>

          <Stack spacing={2}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                >
                  PATIENT
                </Typography>
                <Typography variant="body1">{data.patientName}</Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                >
                  DATE
                </Typography>
                <Typography variant="body1">{data.prescriptionDate}</Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                >
                  DOCTOR
                </Typography>
                <Typography variant="body1">
                  Dr. {data.doctorName}
                </Typography>
                {data.doctorSpecialization && (
                  <Typography variant="body2" color="text.secondary">
                    {data.doctorSpecialization}
                  </Typography>
                )}
              </Box>
              {data.doctorRegistrationNumber && (
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                  >
                    REGISTRATION NO.
                  </Typography>
                  <Typography variant="body1">
                    {data.doctorRegistrationNumber}
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider />

            {data.diagnosis && (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                >
                  DIAGNOSIS
                </Typography>
                <Typography variant="body2">{data.diagnosis}</Typography>
              </Box>
            )}

            {data.medications && data.medications.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{ mb: 0.5, display: "block" }}
                >
                  MEDICATIONS
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Drug</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Dosage</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Frequency</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Instructions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.medications.map((med, i) => (
                        <TableRow key={i}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>{med.drugName}</TableCell>
                          <TableCell>{med.dosage ?? "—"}</TableCell>
                          <TableCell>{med.frequency ?? "—"}</TableCell>
                          <TableCell>{med.duration ?? "—"}</TableCell>
                          <TableCell>{med.instructions ?? "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {data.diagnosticReferral && (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                >
                  DIAGNOSTIC REFERRAL
                </Typography>
                <Typography variant="body2">
                  {data.diagnosticReferral}
                </Typography>
              </Box>
            )}

            {data.remarks && (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                >
                  REMARKS
                </Typography>
                <Typography variant="body2">{data.remarks}</Typography>
              </Box>
            )}
          </Stack>

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            This prescription was issued via ChikitsaLink Telemedicine. Please
            ensure that the URL in your browser address bar is from the official
            ChikitsaLink domain.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
